
import { Component, Vue } from 'vue-property-decorator'
import ProblemPassageCurriculumUnit from '@/components/templates/ProblemPassageCurriculumUnit.vue'
import { LearningProblemCurriculumUnit } from '@/models/LearningProblemCurriculumUnit'
import { ApiNotFoundAppError } from '@/models/AppErrorClasses'
import { LearningProgress } from '@/const/LearningProgress'
import { GeneralLearningsApi } from '@/api/methods/GeneralLearningsApi'
import { UserGeneralProgressesApi } from '@/api/methods/UserGeneralProgressesApi'
import { CurriculumUnitsApi } from '@/api/methods/CurriculumUnitsApi'
import { FixedExercisesApi } from '@/api/methods/FixedExercisesApi'
import { ApiResponseFixedExerciseProblem } from '@/api/responses/fixedExercises'

/**
 * 通常授業サポート学習単元別大問選択画面
 */
@Component({
  components: {
    ProblemPassageCurriculumUnit,
  },
})
export default class SelectGeneralProblemCurriculumUnit extends Vue {
  /** 読み込み完了フラグ */
  private isLoaded = false

  /** 単元別問題一覧 */
  private curriculumUnitProblems?: LearningProblemCurriculumUnit.ProblemPassage

  /**
   * ログイン中のユーザID
   *
   * @returns ユーザID
   */
  private get userId(): number {
    return this.$appCookies.authStudent.get().userId
  }

  /**
   * パラメータから取得した単元ID
   *
   * @returns 単元ID
   */
  private get curriculumUnitId(): number {
    return Number(this.$route.params.curriculumUnitId)
  }

  /**
   * クエリ保持の教材ID
   *
   * @returns 教材ID
   */
  private get studyCategoryId(): number {
    return Number(this.$route.query.studyCategoryId)
  }

  /**
   * クエリ保持の系統図ID
   *
   * @returns 系統図ID
   */
  private get systemChartId(): number {
    const prevPage = this.$prevPage.restorePage()
    if (prevPage) {
      // 戻り先ページ情報が存在する場合
      return Number(prevPage.query.systemChartId)
    }
    return Number(this.$route.query.systemChartId)
  }

  /**
   * 初期処理
   */
  private async mounted(): Promise<void> {
    this.$loading.start()

    // 進捗履歴一覧取得
    const histories = await GeneralLearningsApi.getPassage(this.userId, this.curriculumUnitId)
    const problemHistories: LearningProblemCurriculumUnit.History[] = histories.map((history) => {
      return {
        id: history.id,
        problemId: history.resultProblem.problemId,
        problemCode: history.resultProblem.problemCode,
        level: history.level,
      }
    })

    // 次に演習する学習レベル取得
    const nextProgress = await UserGeneralProgressesApi.getNextLearning(this.curriculumUnitId)
    let nextLearningProgress = LearningProgress.findByCode(nextProgress.learningProgressCode)
    const problemNext: LearningProblemCurriculumUnit.Next = {
      learningProgress: nextLearningProgress,
    }

    // 単元情報取得
    const curriculumUnit = await CurriculumUnitsApi.getGeneral(this.curriculumUnitId)
    let progress = LearningProgress.findByCode(curriculumUnit.progress)

    // 単元別問題一覧の型に格納
    this.curriculumUnitProblems = {
      id: curriculumUnit.id,
      name: curriculumUnit.name,
      learningProgress: progress,
      gradeCode: curriculumUnit.gradeCode,
      subjectCode: curriculumUnit.subjectCode,
      problemHistories: problemHistories,
      problemNext: problemNext,
    }

    this.isLoaded = true

    this.$loading.complete()
  }

  /**
   * 次の演習問題クリック時のイベント
   */
  private async eventClickNextProblem(): Promise<void> {
    // クエリのセット
    let query = {}
    if (this.studyCategoryId) {
      // 教材ID
      query = { studyCategoryId: `${this.studyCategoryId}` }
    } else if (this.systemChartId) {
      // 系統図ID
      query = { systemChartId: `${this.systemChartId}` }
    }

    await this.$router.push({
      path: `/student/general/study/auto/${this.curriculumUnitId}`,
      query: query,
    })
  }

  /**
   * 履歴問題クリック時の処理
   *
   * @param problemCode 問題コード
   * @param level 学習レベル
   */
  private async eventClickHistoryProblem(problemCode: string, level: number): Promise<void> {
    // 固定問題情報を取得
    const fixedExercise = await FixedExercisesApi.getActive(this.curriculumUnitId, level)

    // 小問情報を取得
    let problem!: ApiResponseFixedExerciseProblem
    try {
      problem = await FixedExercisesApi.getProblem(fixedExercise.id, problemCode)
    } catch (error) {
      if (error instanceof ApiNotFoundAppError) {
        alert('この問題は現在演習できません。')
        this.$loading.complete()
        return
      }

      // 404エラー以外の場合は例外を投げ直し、共通処理に渡す
      throw error
    }

    // クエリのセット
    let query = {}
    if (this.studyCategoryId) {
      // 教材ID
      query = { studyCategoryId: `${this.studyCategoryId}` }
    } else if (this.systemChartId) {
      // 系統図ID
      query = { systemChartId: `${this.systemChartId}` }
    }

    await this.$router.push({
      path: `/student/general/study/${fixedExercise.id}/${problem.id}`,
      query: query,
    })
  }

  /**
   * 履歴ボタン押下時の処理
   *
   * @param id 学習履歴ID
   */
  private eventClickHistroyButton(id: number): void {
    this.$router.push({
      path: `/student/general/history/${id}`,
      query: this.$prevPage.generateQuery(),
    })
  }

  /**
   * 戻るボタン押下時の処理
   */
  private async eventClickBackButton(): Promise<void> {
    // 戻り先ページ情報保持の場合
    const returnPage = this.$prevPage.restorePage()
    if (returnPage) {
      await this.$router.push({
        path: returnPage.path,
        query: returnPage.query,
      })
      return
    }

    // 教材ID保持の場合
    if (this.studyCategoryId) {
      await this.$router.push({
        path: '/student/general',
        query: { studyCategoryId: `${this.studyCategoryId}` },
      })
      return
    }

    // 系統図ID保持の場合
    if (this.systemChartId) {
      await this.$router.push({
        path: '/student/map',
        query: { systemChartId: `${this.systemChartId}` },
      })
      return
    }

    // クエリがない場合
    await this.$router.push('/student/top')
  }
}
